// Follow-up reminders: "remind me in 2 days if there's no reply" (migration 082).
//
// Pure rules only: the times on offer, how a time reads, and which reminders My Day
// shows. The database fires them (fire_ticket_reminders, every 5 minutes); this file
// never decides WHEN something is due, only how it is presented.
//
// Times are the viewer's local time (a personal reminder, not an SLA), 9:00 in the
// morning by default. Pure on purpose: tests/reminders.test.ts.

export interface ReminderLike {
  id: string;
  ticket_id: string;
  remind_at: string;
  only_if_no_reply: boolean;
  note: string | null;
  created_at: string;
  fired_at: string | null;
  done_at: string | null;
  cancelled_at: string | null;
}

export type ReminderPresetKey = 'tomorrow' | '2d' | '1w';

export interface ReminderPreset {
  key: ReminderPresetKey;
  label: string;
  at: Date;
}

const REMINDER_HOUR = 9;

function nineAm(now: Date, daysAhead: number): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysAhead, REMINDER_HOUR, 0, 0, 0);
}

export function reminderPresets(now: Date = new Date()): ReminderPreset[] {
  return [
    { key: 'tomorrow', label: 'Tomorrow 9am', at: nineAm(now, 1) },
    { key: '2d', label: 'In 2 days, 9am', at: nineAm(now, 2) },
    { key: '1w', label: 'In a week, 9am', at: nineAm(now, 7) },
  ];
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function clock(d: Date): string {
  const h = d.getHours();
  const m = d.getMinutes();
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** "today 5:00 pm", "tomorrow 9:00 am", "Sat 9:00 am", "28 Oct 9:30 am", "4 Jan 2027 9:00 am". */
export function describeWhen(when: Date | string, now: Date = new Date()): string {
  const d = typeof when === 'string' ? new Date(when) : when;
  const days = Math.round((startOfDay(d) - startOfDay(now)) / 86_400_000);
  const time = clock(d);
  if (days === 0) return `today ${time}`;
  if (days === 1) return `tomorrow ${time}`;
  if (days > 1 && days < 7) return `${DAYS[d.getDay()]} ${time}`;
  const date = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return d.getFullYear() === now.getFullYear() ? `${date} ${time}` : `${date} ${d.getFullYear()} ${time}`;
}

export interface ReminderTicket {
  id: string;
  status: string | null;
  archived_at: string | null;
}

/**
 * My Day's two lists: reminders that have FIRED and are not done ("due"), and the ones still
 * to come ("upcoming"). Done and cancelled reminders, and reminders on a ticket that has since
 * been resolved, closed or archived, are left out. A reminder whose ticket has not loaded yet
 * is kept: never hide something on a guess.
 */
export function splitReminders<T extends ReminderLike>(
  reminders: readonly T[],
  tickets: ReadonlyMap<string, ReminderTicket>,
): { due: T[]; upcoming: T[] } {
  const due: T[] = [];
  const upcoming: T[] = [];
  for (const r of reminders) {
    if (r.done_at || r.cancelled_at) continue;
    const t = tickets.get(r.ticket_id);
    if (t && (t.status === 'resolved' || t.status === 'closed' || t.archived_at)) continue;
    (r.fired_at ? due : upcoming).push(r);
  }
  due.sort((a, b) => a.remind_at.localeCompare(b.remind_at));
  upcoming.sort((a, b) => a.remind_at.localeCompare(b.remind_at));
  return { due, upcoming };
}

/** 9:00 local, `days` days after `now` (for a macro's "remind me in N days"). */
export function reminderAtDays(now: Date, days: number): Date {
  return nineAm(now, days);
}
